import * as THREE from 'three';
import {
  bevelBox, extrudeShape, filletShape, roundedRectShape, chamferCyl,
  stippleField, picatinnyRail, mlokPanel,
} from './Bevel.js';
import { place } from './Builder.js';
import {
  sideProfile, frontProfile, cylZ, coneZ, tubeZ, decal, screwY, screwX, crossPin, knurl,
  redDotOptic, backupSights, stanagMag, pistolGrip, triggerGroup, barrelAssembly,
} from './Kit.js';

/**
 * M4A1-class carbine. Real dimensions: 756 mm collapsed, 368 mm barrel,
 * 192 mm magazine, bore on Y = 0, rail top 29.6 mm over bore, optic axis 65.5 mm.
 */

const RAIL_BASE = 0.0205;
const RAIL_TOP = RAIL_BASE + 0.0091;
const OPTIC_AXIS = 0.0655;

/** Hollow rounded-box tube with its axis on Y, top face at y = 0, going down. */
export function boxTubeY(w, d, wall, h, bevel = 0.0008) {
  const outer = roundedRectShape(w, d, 0.0035, 5);
  const inner = roundedRectShape(w - wall * 2, d - wall * 2, 0.0025, 5);
  const holePts = inner.getPoints(24).reverse();
  const hole = new THREE.Path();
  hole.moveTo(holePts[0].x, holePts[0].y);
  for (let i = 1; i < holePts.length; i++) hole.lineTo(holePts[i].x, holePts[i].y);
  hole.closePath();
  outer.holes.push(hole);
  const g = extrudeShape(outer, h, { bevel, curveSegments: 5 });
  g.rotateX(Math.PI / 2);
  g.translate(0, -h / 2, 0);
  return g;
}

export function buildRifle(B, rng) {
  /* ---------------- carbon fouling ----------------
   * Declared before any geometry so every part picks up its own soot value.
   * These are the three places a direct-impingement carbine is actually filthy:
   * around the ejection port and bolt face, at the gas block, and at the crown.
   */
  /*
   * Strengths cut from 1.00/0.55/0.70/0.75. The bolt carrier, the barrel and
   * the flash hider are BARE METAL now, and `aGrime` drives metalness down by
   * 0.75x and roughness up by 0.20 — so at full soot the one genuinely
   * specular surface on the weapon was being turned back into a dielectric
   * exactly where the closeup camera looks at it. A carbine that has been
   * fired is filthy in the gas path, but the carrier rails and the barrel
   * flats are burnished bright by cycling, which is why a real BCG photographs
   * as streaks of soot over steel rather than as a black bar.
   */
  B.sootSource(0.0200, 0.0090, -0.0760, 0.0080, 0.0560, 0.55);   // ejection port
  B.sootSource(0.0000, 0.0030, -0.1050, 0.0090, 0.0440, 0.40);   // chamber / feed ramp
  B.sootSource(0.0000, 0.0110, -0.3960, 0.0110, 0.0420, 0.50);   // gas block
  B.sootSource(0.0000, 0.0000, -0.5150, 0.0120, 0.0460, 0.55);   // flash hider

  /* ---------------- hand contact ----------------
   * FINDINGS F20's second gap is "roughness that varies across a single surface".
   * On a weapon the honest source of that variation is not noise — it is where the
   * thing is TOUCHED. A carried rifle is burnished bright and smooth on the grip
   * tang, the handguard where the support hand wraps, the charging-handle latch,
   * the selector, the mag release and the magwell mouth a magazine is slammed into,
   * and it is matt everywhere else. Every one of those places is a known point in
   * bind-pose weapon space, so the field costs one float per vertex baked once at
   * build time and nothing at all at runtime (FINDINGS F6: no fifth sampler).
   *
   * Radii are the size of the contact patch, not of the part: a hand covers ~5 cm
   * of a grip, a thumb ~1.5 cm of a selector.
   */
  B.touchSource(0.0000, -0.0761, 0.0332, 0.0240, 0.0560, 1.00);  // pistol grip
  B.touchSource(0.0000, -0.0300, -0.3050, 0.0230, 0.0640, 0.90);  // support hand, handguard
  B.touchSource(0.0000, 0.0000, -0.3450, 0.0180, 0.0520, 0.60);  // support-hand thumb over
  B.touchSource(-0.0230, 0.0160, 0.0180, 0.0090, 0.0280, 0.95);  // charging handle latch
  B.touchSource(-0.0195, -0.0230, -0.0130, 0.0060, 0.0210, 0.90);  // selector
  B.touchSource(0.0198, -0.0330, -0.0800, 0.0050, 0.0180, 0.85);  // mag release
  B.touchSource(-0.0198, -0.0270, -0.0790, 0.0050, 0.0180, 0.70);  // bolt catch
  B.touchSource(0.0000, -0.0300, -0.1035, 0.0140, 0.0330, 0.75);  // magwell mouth
  B.touchSource(0.0000, 0.0140, 0.1250, 0.0170, 0.0460, 0.55);  // cheek weld, stock comb
  B.touchSource(0.0000, -0.0320, -0.0330, 0.0060, 0.0180, 0.80);  // trigger face

  /* ---------------- skeleton ---------------- */
  B.bone('root');
  B.bone('receiver', 'root');
  B.bone('bolt', 'receiver', [0, 0.0015, -0.070]);
  B.bone('charging', 'receiver', [0, 0.0160, 0.004]);
  B.bone('dust', 'receiver', [0.0190, 0.0008, -0.075]);
  B.bone('mag', 'receiver', [0, -0.0255, -0.1035]);
  B.bone('trigger', 'receiver', [0, -0.0330, -0.0330]);
  B.bone('selector', 'receiver', [-0.0195, -0.0230, -0.0130]);
  B.bone('boltcatch', 'receiver', [-0.0198, -0.0270, -0.0790]);
  B.bone('magrelease', 'receiver', [0.0198, -0.0330, -0.0800]);
  B.bone('stock', 'receiver', [0, -0.0040, 0.1250]);
  B.bone('optic', 'receiver');
  B.bone('sights', 'receiver');

  /* ---------------- upper receiver ---------------- */
  const upper = [
    [-0.0158, -0.0178], [0.0158, -0.0178], [0.0192, -0.0062], [0.0192, 0.0126],
    [0.0134, 0.0205], [-0.0134, 0.0205], [-0.0192, 0.0126], [-0.0192, -0.0062],
  ];
  const portTop = 0.0176, portBot = 0.0006, portX = 0.0104;
  const upperPort = [
    [-0.0158, -0.0178], [0.0158, -0.0178], [0.0192, -0.0062], [0.0192, portBot],
    [portX, portBot], [portX, portTop], [0.0176, portTop], [0.0134, 0.0205],
    [-0.0134, 0.0205], [-0.0192, 0.0126], [-0.0192, -0.0062],
  ];
  const seg = (pts, z0, z1) => {
    const g = frontProfile(pts, 0.0024, z1 - z0, 0.0008, 4);
    return g.translate(0, 0, (z0 + z1) / 2);
  };
  B.add([
    seg(upper, -0.1985, -0.0995),
    seg(upperPort, -0.0995, -0.0505),
    seg(upper, -0.0505, 0.0),
  ], 'alloy', 'receiver', null, { edgeWear: 0.45 });

  // rear plate / charging-handle channel walls
  B.add(frontProfile([
    [-0.0192, -0.0062], [0.0192, -0.0062], [0.0192, 0.0126], [0.0134, 0.0205],
    [-0.0134, 0.0205], [-0.0192, 0.0126],
  ], 0.0022, 0.0090, 0.0007, 4).translate(0, 0, 0.0044), 'alloy', 'receiver', null, { edgeWear: 0.7 });

  // ejection port lip and brass deflector
  B.add(sideProfile([
    [-0.1010, portBot - 0.0022], [-0.0490, portBot - 0.0022],
    [-0.0490, portBot + 0.0010], [-0.1010, portBot + 0.0010],
  ], 0.0008, 0.0090, 0.0005), 'bare', 'receiver',
  place(0.0192 - 0.0038, 0, 0), { edgeWear: 0.9 });
  B.add(frontProfile([
    [0.0140, 0.0020], [0.0268, 0.0086], [0.0268, 0.0170], [0.0140, 0.0170],
  ], 0.0016, 0.0250, 0.0011, 3), 'alloy', 'receiver',
  place(0, 0, -0.0330), { edgeWear: 0.8 });

  // forward assist
  B.add(cylZ(0.0072, 0.0170, 0.0007, 16), 'bare', 'receiver',
    place(0.0212, 0.0060, -0.0180, 0, -0.16, 0), { edgeWear: 0.7 });
  B.add(cylZ(0.0048, 0.0110, 0.0006, 14), 'bare', 'receiver',
    place(0.0212, 0.0060, -0.0070, 0, -0.16, 0), { edgeWear: 0.95 });
  for (let i = 0; i < 3; i++) {
    B.add(cylZ(0.0050, 0.0012, 0.0003, 14), 'bare', 'receiver',
      place(0.0212, 0.0060, -0.0040 + i * 0.0022, 0, -0.16, 0), { wear: 0.5 });
  }

  // top rail over the receiver
  const railR = picatinnyRail(0.1990);
  B.add(railR.parts, 'metal', 'receiver', place(0, RAIL_BASE, -0.0995), { edgeWear: 0.85 });

  // charging handle: latch on the left, T ring at the rear
  B.add([
    bevelBox(0.0230, 0.0060, 0.0330, 0.0006).translate(0, 0, -0.0110),
    bevelBox(0.0092, 0.0052, 0.0620, 0.0005).translate(0, 0.0002, -0.0480),
  ], 'bare', 'charging', place(0, 0.0160, 0.0040), { edgeWear: 0.85 });
  B.add([
    bevelBox(0.0165, 0.0048, 0.0125, 0.0006).translate(-0.0090, 0, 0),
    bevelBox(0.0040, 0.0072, 0.0110, 0.0005).translate(-0.0155, -0.0010, 0),
  ], 'bare', 'charging', place(0, 0.0160, -0.0018), { edgeWear: 1.0 });
  for (let i = 0; i < 5; i++) {
    B.add(bevelBox(0.0012, 0.0068, 0.0014, 0.0003), 'bare', 'charging',
      place(-0.0176, 0.0150, -0.0060 + i * 0.0026), { wear: 0.6 });
  }

  /* ---------------- bolt carrier, seen through the port ---------------- */
  B.add([
    cylZ(0.0148, 0.1180, 0.0010, 22),
    bevelBox(0.0210, 0.0110, 0.0640, 0.0008).translate(0, 0.0060, 0.0180),
  ], 'bare', 'bolt', place(0, 0.0015, -0.0700), { wear: 0.55 });
  // gas key
  B.add(bevelBox(0.0130, 0.0088, 0.0210, 0.0007), 'bare', 'bolt',
    place(0, 0.0150, -0.0570), { edgeWear: 0.8, wear: 0.3 });
  // bolt face + extractor, polished by use
  B.add(cylZ(0.0110, 0.0090, 0.0008, 20), 'bare', 'bolt',
    place(0, 0.0015, -0.1245), { wear: 0.85 });
  B.add(bevelBox(0.0052, 0.0072, 0.0170, 0.0005), 'bare', 'bolt',
    place(0.0086, 0.0060, -0.1200), { edgeWear: 0.9, wear: 0.5 });
  // cam pin
  B.add(chamferCyl(0.0042, 0.0060, 0.0005, 14), 'bare', 'bolt',
    place(0, 0.0130, -0.0930), { wear: 0.7 });

  /* ---------------- dust cover ---------------- */
  B.add([
    bevelBox(0.0034, 0.0212, 0.0505, 0.0011).translate(0, 0.0110, 0),
    bevelBox(0.0022, 0.0044, 0.0530, 0.0008).translate(-0.0012, 0.0198, 0),
  ], 'steel', 'dust', place(0.0190, 0.0008, -0.0750), { edgeWear: 0.8 });
  for (let i = 0; i < 4; i++) {
    B.add(bevelBox(0.0014, 0.0150, 0.0018, 0.0004), 'steel', 'dust',
      place(0.0206, 0.0110, -0.0900 + i * 0.0100), { wear: 0.5 });
  }
  // hinge rod
  B.add(cylZ(0.0022, 0.0560, 0.0003, 12), 'steel', 'dust',
    place(0.0190, 0.0008, -0.0750), { wear: 0.4 });

  /* ---------------- lower receiver ---------------- */
  const lower = [
    [-0.0800, -0.0178], [-0.0800, -0.0348], [-0.0330, -0.0338],
    [0.0120, -0.0316], [0.0330, -0.0300], [0.0330, -0.0178],
  ];
  B.add(sideProfile(lower, 0.0042, 0.0378, 0.0014, 4), 'metal', 'receiver', null, { edgeWear: 0.4 });
  // buffer tower
  B.add(sideProfile([
    [0.0040, -0.0300], [0.0330, -0.0300], [0.0330, 0.0060], [0.0040, 0.0060],
  ], 0.0040, 0.0330, 0.0014, 4), 'metal', 'receiver', null, { edgeWear: 0.55 });

  // magwell
  B.add(boxTubeY(0.0360, 0.0500, 0.0050, 0.0530, 0.0018), 'metal', 'receiver',
    place(0, -0.0178, -0.1040), { edgeWear: 0.5 });
  // flared magwell lip
  B.add(boxTubeY(0.0396, 0.0540, 0.0068, 0.0070, 0.0020), 'metal', 'receiver',
    place(0, -0.0680, -0.1040), { edgeWear: 0.95 });
  // front of the magwell blends into the lower
  B.add(sideProfile([
    [-0.1295, -0.0178], [-0.0790, -0.0178], [-0.0790, -0.0420], [-0.1295, -0.0420],
  ], 0.0034, 0.0360, 0.0014, 3), 'metal', 'receiver', null, { edgeWear: 0.4 });

  // takedown + pivot pins
  B.add(crossPin(-0.0250, 0.0230, 0.0030, 0.0190), 'bare', 'receiver', null, { edgeWear: 0.9, wear: 0.35 });
  B.add(crossPin(-0.0250, -0.1180, 0.0030, 0.0182), 'bare', 'receiver', null, { edgeWear: 0.9, wear: 0.35 });

  /* ---------------- lower-receiver relief ----------------
   *
   * The reviewer's standing note: "the lower receiver is a large, flat,
   * near-featureless surface — the weakest material in frame". It was one
   * extruded side profile 38 mm wide and 90 mm long with nothing on it, and no
   * amount of material authoring rescues a surface that has no form to catch a
   * highlight. A real forged lower is not flat anywhere: every pin is in a
   * boss, the mag release and bolt catch sit in raised fences, the magwell has
   * a reinforcing web at the front and a step where it meets the trigger
   * housing, and the flank carries a shallow relief either side of the serial.
   *
   * All of it is `metal` (the same anodising lot) except the pin heads, so it
   * adds FORM, not colour — which is the whole thesis of this iteration.
   */
  {
    const LX = 0.0189;                    // the lower's flank, half-width
    // fences around the two controls: a raised pad the head sits proud of
    const fence = (x, y, z, h, w, s) => B.add(
      sideProfile([[z - w, y - h], [z + w, y - h], [z + w, y + h], [z - w, y + h]],
        0.0038, 0.0032, 0.0009, 4), 'metal', 'receiver',
      place(s * (LX + 0.0016), 0, 0), { edgeWear: 0.75 });
    fence(0, -0.0330, -0.0800, 0.0105, 0.0112, 1);   // magazine release, right
    fence(0, -0.0272, -0.0790, 0.0112, 0.0126, -1);  // bolt catch, left

    // pin bosses: a shallow raised ring around every cross pin, both flanks
    for (const [y, z, r] of [[-0.0250, 0.0230, 0.0068], [-0.0250, -0.1180, 0.0066],
      [-0.0330, -0.0330, 0.0050], [-0.0330, -0.0510, 0.0048]]) {
      for (const s of [-1, 1]) {
        B.add(chamferCyl(r, 0.0022, 0.0006, 16).rotateZ(Math.PI / 2), 'metal', 'receiver',
          place(s * (LX + 0.0004), y, z), { edgeWear: 0.85 });
      }
    }
    // trigger and hammer pins themselves — small bright steel, correctly placed
    for (const z of [-0.0330, -0.0510]) {
      B.add(crossPin(-0.0330, z, 0.0026, LX + 0.0018), 'bare', 'receiver', null,
        { edgeWear: 0.9, wear: 0.30 });
    }

    // magwell reinforcing web at the front, and the step into the trigger housing
    for (const s of [-1, 1]) {
      B.add(sideProfile([
        [-0.1300, -0.0210], [-0.1215, -0.0250], [-0.1215, -0.0640], [-0.1300, -0.0600],
      ], 0.0022, 0.0038, 0.0009, 3), 'metal', 'receiver',
      place(s * (LX - 0.0010), 0, 0), { edgeWear: 0.7 });
      // the shelf where the magwell body meets the trigger housing
      B.add(sideProfile([
        [-0.0800, -0.0300], [-0.0330, -0.0286], [-0.0330, -0.0254], [-0.0800, -0.0268],
      ], 0.0014, 0.0034, 0.0008, 3), 'metal', 'receiver',
      place(s * (LX - 0.0008), 0, 0), { edgeWear: 0.8 });
      // shallow relief panel under the serial, the flat the roll mark sits in
      B.add(sideProfile([
        [-0.1120, -0.0232], [-0.0850, -0.0232], [-0.0850, -0.0420], [-0.1120, -0.0420],
      ], 0.0044, 0.0022, 0.0008, 4), 'metal', 'receiver',
      place(s * (LX - 0.0006), 0, 0), { edgeWear: 0.55 });
    }
    // top edge of the lower: the machined step the upper's flanges close onto
    for (const s of [-1, 1]) {
      B.add(sideProfile([
        [-0.0790, -0.0186], [0.0320, -0.0186], [0.0320, -0.0156], [-0.0790, -0.0156],
      ], 0.0008, 0.0030, 0.0007, 3), 'metal', 'receiver',
      place(s * (LX - 0.0004), 0, 0), { edgeWear: 0.95 });
    }
    // tension screw / rear takedown detent housing behind the selector
    B.add(chamferCyl(0.0044, 0.0026, 0.0006, 14).rotateZ(Math.PI / 2), 'metal', 'receiver',
      place(LX + 0.0006, -0.0286, 0.0090), { edgeWear: 0.8 });
    B.add(screwX(LX + 0.0026, -0.0286, 0.0090, 1, 0.0022, 0.0014), 'bare', 'receiver',
      null, { wear: 0.45 });
  }

  /* ---------------- controls ---------------- */
  // selector lever, ambidextrous
  for (const s of [-1, 1]) {
    B.add([
      chamferCyl(0.0072, 0.0042, 0.0005, 16).rotateZ(Math.PI / 2),
      bevelBox(0.0038, 0.0075, 0.0230, 0.0006).translate(0, -0.0010, 0.0100),
    ], 'metal', 'selector', place(s * 0.0196, -0.0230, -0.0130, 0, 0, 0), { edgeWear: 0.9 });
  }
  // bolt catch, left
  B.add([
    bevelBox(0.0042, 0.0090, 0.0360, 0.0006),
    bevelBox(0.0060, 0.0150, 0.0105, 0.0007).translate(-0.0008, -0.0020, -0.0125),
  ], 'metal', 'boltcatch', place(-0.0198, -0.0270, -0.0790), { edgeWear: 0.9 });
  for (let i = 0; i < 4; i++) {
    B.add(bevelBox(0.0014, 0.0130, 0.0014, 0.0003), 'metal', 'boltcatch',
      place(-0.0230, -0.0290, -0.0860 + i * 0.0022), { wear: 0.6 });
  }
  // magazine release, right
  B.add([
    chamferCyl(0.0062, 0.0090, 0.0006, 16).rotateZ(Math.PI / 2),
    chamferCyl(0.0092, 0.0038, 0.0007, 16).rotateZ(Math.PI / 2).translate(-0.0035, 0, 0),
  ], 'metal', 'magrelease', place(0.0198, -0.0330, -0.0800), { edgeWear: 0.85 });
  B.add(knurl(0.0044, 0.0026, 10, 0.0008), 'metal', 'magrelease',
    place(0.0242, -0.0330, -0.0800, 0, 0, -Math.PI / 2), { wear: 0.55 });

  /* ---------------- trigger + guard ---------------- */
  triggerGroup(B, 'receiver', 'trigger', { z: -0.0330, y: -0.0300, width: 0.0122 });

  /* ---------------- pistol grip ---------------- */
  const grip = pistolGrip(B, 'receiver', rng, { tilt: 0.44, length: 0.1060, y: -0.0290, z: 0.0110 });
  B.add(screwY(0, -0.0290 - 0.1060 * Math.cos(0.44) - 0.0035,
    0.0110 + 0.1060 * Math.sin(0.44), 0.0032, 0.0018), 'bare', 'receiver', null, { wear: 0.5 });

  /* ---------------- magazine ---------------- */
  const mag = stanagMag(B, 'mag', {
    mat: 'polymer', length: 0.1920, at: place(0, -0.0255, -0.1035),
  });

  /* ---------------- handguard ---------------- */
  const hgZ0 = -0.2000, hgZ1 = -0.4280;
  const hgLen = hgZ0 - hgZ1;
  const hgZc = (hgZ0 + hgZ1) / 2;
  const R = 0.0205;
  const faceW = 2 * R * Math.tan(Math.PI / 8);
  const wall = 0.0038;

  for (let k = 0; k < 8; k++) {
    const a = (k / 8) * Math.PI * 2;
    if (Math.abs(Math.sin(a) - 1) < 0.01) continue; // the top face carries the rail
    const cx = Math.cos(a) * (R - wall / 2), cy = Math.sin(a) * (R - wall / 2);
    const slotted = Math.abs(Math.cos(a)) > 0.7 || Math.abs(Math.sin(a) + 1) < 0.01;
    const m = place(cx, cy, hgZc, 0, 0, a - Math.PI / 2);
    if (slotted) {
      B.add(mlokPanel(hgLen, faceW * 1.02, wall, {
        slotLength: 0.0320, slotWidth: 0.0072, pitch: 0.0400, count: 4, bevel: 0.0007,
      }), 'polymer_fde', 'receiver', m, { edgeWear: 0.55 });
    } else {
      B.add(bevelBox(faceW * 1.02, wall, hgLen, 0.0010), 'polymer_fde', 'receiver', m, { edgeWear: 0.5 });
    }
  }
  // Top face of the handguard. The rail runs in two sections — a long one continuous
  // with the receiver and a short front block for the folding sight — leaving a bare
  // stretch exactly where the support hand grips, so the fingers never have to close
  // through a rail.
  B.add(bevelBox(faceW * 1.15, wall, hgLen, 0.0010), 'polymer_fde', 'receiver',
    place(0, R - wall / 2, hgZc), { edgeWear: 0.5 });
  const railRearLen = 0.0880, railFrontLen = 0.0520;
  B.add(picatinnyRail(railRearLen).parts, 'metal', 'receiver',
    place(0, R, hgZ0 - railRearLen / 2 - 0.0015), { edgeWear: 0.85 });
  B.add(picatinnyRail(railFrontLen).parts, 'metal', 'receiver',
    place(0, R, hgZ1 + railFrontLen / 2 + 0.0060), { edgeWear: 0.85 });

  // barrel-nut collar and muzzle-end collar
  B.add(tubeZ(R + 0.0016, R - wall - 0.0010, 0.0130, 0.0009, 24), 'bare', 'receiver',
    place(0, 0, hgZ0 - 0.0060), { edgeWear: 0.7 });
  B.add(tubeZ(R + 0.0010, R - wall - 0.0010, 0.0100, 0.0009, 24), 'bare', 'receiver',
    place(0, 0, hgZ1 + 0.0050), { edgeWear: 0.8 });
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    B.add(screwY(0, 0, 0, 0.0020, 0.0013), 'bare', 'receiver',
      place(Math.cos(a) * (R + 0.0002), Math.sin(a) * (R + 0.0002), hgZ0 - 0.0060,
        0, 0, a - Math.PI / 2), { wear: 0.5 });
  }
  // inner heat shield showing through the M-LOK slots
  B.add(tubeZ(R - wall - 0.0032, R - wall - 0.0050, hgLen - 0.0180, 0.0006, 20), 'rubber', 'receiver',
    place(0, 0, hgZc), {});

  /* ---------------- barrel, gas block ---------------- */
  const barrel = barrelAssembly(B, 'receiver', { front: -0.4720, back: -0.1900 });
  B.add([
    bevelBox(0.0250, 0.0230, 0.0300, 0.0008),
    cylZ(0.0056, 0.0620, 0.0005, 14).translate(0, 0.0092, 0.0290),
  ], 'steel', 'receiver', place(0, 0.0010, -0.3960), { edgeWear: 0.5, wear: 0.35 });
  for (const s of [-1, 1]) {
    B.add(screwX(s * 0.0126, -0.0060, -0.3960, s, 0.0022, 0.0014), 'bare', 'receiver', null, { wear: 0.5 });
  }

  /* ---------------- optic + irons ---------------- */
  const optic = redDotOptic(B, 'optic', { railTop: RAIL_TOP, axis: OPTIC_AXIS, z: -0.0640 });
  backupSights(B, 'sights', { railTop: RAIL_TOP, axis: OPTIC_AXIS, rearZ: -0.0110, frontZ: -0.3960 });

  /* ---------------- buffer tube + collapsible stock ---------------- */
  B.add(cylZ(0.0148, 0.1880, 0.0009, 24), 'metal', 'receiver',
    place(0, -0.0040, 0.1180), { edgeWear: 0.35 });
  // castle nut + receiver end plate
  B.add(chamferCyl(0.0182, 0.0090, 0.0008, 6).rotateX(Math.PI / 2), 'bare', 'receiver',
    place(0, -0.0040, 0.0270), { edgeWear: 0.9 });
  B.add(bevelBox(0.0330, 0.0330, 0.0032, 0.0007), 'bare', 'receiver',
    place(0, -0.0040, 0.0200), { edgeWear: 0.9 });
  // sling loop on the end plate
  B.add(tubeZ(0.0058, 0.0034, 0.0028, 0.0004, 14), 'bare', 'receiver',
    place(-0.0170, 0.0035, 0.0200, 0, Math.PI / 2, 0), { edgeWear: 0.9, wear: 0.4 });
  // stock-position detents along the underside of the buffer tube
  for (let i = 0; i < 6; i++) {
    B.add(chamferCyl(0.0026, 0.0022, 0.0004, 12), 'bare', 'receiver',
      place(0, -0.0186, 0.0700 + i * 0.0210), { wear: 0.3 });
  }

  // stock body (slides on the buffer tube)
  const SB = place(0, -0.0040, 0.1250);
  B.add([
    // main shell: sloped underside, raised comb, flared butt
    sideProfile([
      [-0.0760, -0.0185], [-0.0500, -0.0235], [-0.0170, -0.0330], [0.0260, -0.0362],
      [0.0620, -0.0330], [0.0770, -0.0250], [0.0790, 0.0120], [0.0610, 0.0198],
      [0.0100, 0.0208], [-0.0340, 0.0170], [-0.0760, 0.0132],
    ], 0.0060, 0.0442, 0.0014, 5),
    // cheek weld strap running along the comb
    sideProfile([
      [-0.0300, 0.0165], [0.0480, 0.0200], [0.0480, 0.0248], [-0.0300, 0.0212],
    ], 0.0032, 0.0300, 0.0011, 3),
    // front collar that actually wraps the buffer tube
    sideProfile([
      [-0.1050, -0.0190], [-0.0740, -0.0210], [-0.0740, 0.0140], [-0.1050, 0.0120],
    ], 0.0038, 0.0330, 0.0012, 3),
  ], 'polymer_grey', 'stock', SB, { edgeWear: 0.30 });

  // lightening cuts and the sling slot on both flanks
  for (const s of [-1, 1]) {
    B.add(sideProfile([
      [-0.0420, -0.0140], [0.0400, -0.0210], [0.0400, 0.0090], [-0.0420, 0.0050],
    ], 0.0055, 0.0060, 0.0010, 4), 'polymer_grey', 'stock',
    place(s * 0.0208, -0.0040, 0.1250), { edgeWear: 0.45 });
    B.add(sideProfile([
      [0.0080, -0.0270], [0.0430, -0.0290], [0.0430, -0.0180], [0.0080, -0.0165],
    ], 0.0035, 0.0080, 0.0009, 3), 'polymer_grey', 'stock',
    place(s * 0.0196, -0.0040, 0.1250), { edgeWear: 0.7 });
  }
  // release lever under the tube
  B.add(sideProfile([
    [-0.0640, -0.0330], [-0.0250, -0.0360], [-0.0250, -0.0470], [-0.0620, -0.0450],
  ], 0.0040, 0.0140, 0.0010, 3), 'polymer_grey', 'stock', SB, { edgeWear: 0.6 });
  B.add(cylZ(0.0034, 0.0150, 0.0005, 12), 'bare', 'stock',
    place(0, -0.0480, 0.0850, 0, Math.PI / 2, 0), { wear: 0.4 });

  // rubber buttpad with real ribs
  B.add(sideProfile([
    [0.0780, -0.0270], [0.0885, -0.0180], [0.0895, 0.0080], [0.0780, 0.0135],
  ], 0.0038, 0.0452, 0.0014, 3), 'rubber', 'stock', SB, { edgeWear: 0.25 });
  for (let i = 0; i < 5; i++) {
    B.add(bevelBox(0.0440, 0.0030, 0.0036, 0.0008), 'rubber', 'stock',
      place(0, -0.0230 + i * 0.0088, 0.2143), { wear: 0.15 });
  }
  // QD sling socket
  B.add(chamferCyl(0.0064, 0.0058, 0.0006, 14).rotateZ(Math.PI / 2), 'bare', 'stock',
    place(-0.0216, -0.0190, 0.1560), { edgeWear: 0.8 });
  B.add(chamferCyl(0.0034, 0.0070, 0.0004, 12).rotateZ(Math.PI / 2), 'bare', 'stock',
    place(-0.0224, -0.0190, 0.1560), { wear: 0.5 });

  /* ---------------- roll marks ----------------
   * The ambidextrous selector is marked on BOTH flanks, as the real part is.
   * The closeup camera looks at the right flank; it previously found only the
   * proof stamp there and read it as a selector saying "MP / HP / P".
   */
  B.add(decal('roll_receiver', 0.0560, 'left', -0.0192 - 0.0004, -0.0270, -0.1030),
    'marks', 'receiver', null, { keepUV: true });
  B.add(decal('selector', 0.0300, 'left', -0.0192 - 0.0004, -0.0225, -0.0120),
    'marks', 'receiver', null, { keepUV: true });
  B.add(decal('selector_r', 0.0300, 'right', 0.0192 + 0.0004, -0.0225, -0.0120, { mirror: true }),
    'marks', 'receiver', null, { keepUV: true });
  /* 15.5 mm wide put a 4 mm-tall proof stamp on a receiver flank that is 26 mm
   * deep, and at the closeup framing the two shield pictograms in that cell drew
   * at 60 px each and read as cartoon icons. A real NATO proof stamp is 4-6 mm
   * across the circle. */
  B.add(decal('proof', 0.0098, 'right', 0.0192 + 0.0004, -0.0122, -0.1215),
    'marks', 'receiver', null, { keepUV: true });

  /* ---------------- grip anchors for the hands ---------------- */
  const gripAxis = new THREE.Vector3(0, -1, 0).transformDirection(grip.matrix).normalize();
  const gripCentre = new THREE.Vector3(0, -0.052, 0).applyMatrix4(grip.matrix);

  return {
    name: 'rifle',
    railTop: RAIL_TOP,
    sight: { axis: OPTIC_AXIS, z: optic.sightZ },
    ironSight: { axis: OPTIC_AXIS, z: -0.0110 },
    muzzle: new THREE.Vector3(0, 0, barrel.muzzleZ),
    ejectPort: new THREE.Vector3(0.0240, 0.0100, -0.0740),
    ejectDir: new THREE.Vector3(0.92, 0.36, 0.16).normalize(),
    magBone: 'mag',
    magLength: 0.1920,
    anchors: {
      rightGrip: { point: gripCentre, dir: gripAxis, radius: 0.0182, bone: 'receiver' },
      // `dir` runs thumb -> pinky, so the support thumb ends up pointing forward.
      leftGrip: {
        point: new THREE.Vector3(0, 0, -0.3220),
        dir: new THREE.Vector3(0, 0, 1), radius: 0.0232, bone: 'receiver',
      },
      trigger: new THREE.Vector3(0, -0.0455, -0.0356),
      triggerRest: new THREE.Vector3(0.0165, -0.0330, -0.0640),
      magGrab: new THREE.Vector3(0, -0.1420, -0.0980),
      chargingHandle: new THREE.Vector3(-0.0150, 0.0180, 0.0000),
    },
    grip, mag,
  };
}
